import { ImageUploadUrl } from 'types/graphql'

import { useMutation } from '@cedarjs/web'
import { toast } from '@cedarjs/web/toast'

import { StationInputType, UrlsType } from 'src/common/types'
import CardSection from 'src/components/Custom/Card/CardSection'
import DragAndDropImageArea from 'src/components/Custom/DragAndDropImageArea/DragAndDropImageArea'
import UpdatableText from 'src/components/Custom/UpdatableText/UpdatableText'
import {
  CREATE_IMAGE_UPLOAD_URL_MUTATION,
  ImageProfile,
  uploadImageToCloudflare,
} from 'src/lib/images'

export interface AboutData {
  about: string
  backgroundImagePreviewUrl?: string
  images: Array<UrlsType>
  name: string
}

interface AboutProps {
  data: AboutData
  onChange?: (data: { about?: string; images?: UrlsType[] }) => void
  updateStation: (inputData: StationInputType) => Promise<void>
  loadingUpdateStation: boolean
}

const About = ({
  data,
  onChange,
  updateStation,
  loadingUpdateStation,
}: AboutProps) => {
  const updateData = (value: string, key: string) => {
    const _data = { ...data }
    _data[key] = value
    onChange(_data)
  }

  const [createImageUploadUrl] = useMutation<{
    createImageUploadUrl: ImageUploadUrl
  }>(CREATE_IMAGE_UPLOAD_URL_MUTATION)

  const handleImageUpload = async (files: File[]) => {
    const updatedImages = [...data.images]

    const mutationResult = await createImageUploadUrl()
    const { uploadUrl } = mutationResult.data.createImageUploadUrl

    await toast
      .promise(uploadImageToCloudflare(uploadUrl, files[0]), {
        loading: 'Uploading background image...',
        success: 'Image uploaded successfully',
        error: 'Error uploading image',
      })
      .then((newImageUrl) => {
        const profileToUpdate = ImageProfile.STATION_BACKGROUND

        const imageIndex = updatedImages.findIndex(
          (image) => image.profile === profileToUpdate
        )

        if (imageIndex !== -1) {
          updatedImages[imageIndex] = {
            profile: profileToUpdate,
            url: newImageUrl,
          }
        } else {
          updatedImages.push({ profile: profileToUpdate, url: newImageUrl })
        }

        updateStation({ images: updatedImages }).then(() => {
          onChange({ images: updatedImages })
        })
      })
  }

  const aboutTextValidationRules = {
    required: `Please provide a description of your station's streaming app.`,
    validate: {
      aboutText: (value) => {
        if (!value || value.trim() === '') {
          return `Please provide a description of your station's streaming app.`
        }

        return true
      },
    },
  }

  return (
    <div className="w-full">
      <div className="flex flex-col justify-center">
        <CardSection title="Name">
          <UpdatableText
            value={data.name}
            onChange={(v) => updateData(v, 'name')}
            onSave={() => updateStation({ name: data.name })}
            validationRules={{
              required: 'Station name cannot be empty.',
            }}
            loadingData={loadingUpdateStation}
          />
        </CardSection>

        <CardSection title="About text">
          <UpdatableText
            value={data.about}
            textArea
            onChange={(v) => updateData(v, 'about')}
            onSave={() => updateStation({ about: data.about })}
            validationRules={aboutTextValidationRules}
            loadingData={loadingUpdateStation}
          />
        </CardSection>

        <CardSection title="Background Photo">
          {data?.backgroundImagePreviewUrl ? (
            <img
              className="mb-2 w-full rounded-lg object-cover"
              alt="background"
              src={data.backgroundImagePreviewUrl}
            />
          ) : (
            <em>Not set</em>
          )}
          <DragAndDropImageArea
            id="aboutPage"
            title="Change photo"
            onUpload={handleImageUpload}
          />
        </CardSection>
      </div>
    </div>
  )
}

export default About
